/* eslint-disable react-hooks/exhaustive-deps */

import { FilterTask, TaskList } from "pages/Task/types"
import { useEffect, useState } from "react"
import { SuccessFetch } from "types"
import { useApi } from "utils"

type UseTaskList = {
  params: FilterTask
  onSuccess?: (paging: Response["pagination_data"]) => void
}

type Response = {
  payload: TaskList[]
  pagination_data: {
    total: number
    page: number
    size: number
    pages: number
  }
}

export function useTaskList({ params, onSuccess }: UseTaskList) {
  const api = useApi()
  const [data, setData] = useState<TaskList[]>([])
  const [paging, setPaging] = useState<
    Response["pagination_data"] & { limit: number }
  >()
  const [isLoading, setLoading] = useState(false)

  const fetchData = () => {
    setLoading(true)

    const payload = {
      ...params,
      task_status: params.task_status === "0" ? undefined : params.task_status,
      priority: params.priority === "0" ? undefined : params.priority
    }

    api
      .get("/task/", { params: payload })
      .then((res: SuccessFetch<Response>) => {
        const data = res.data.payload ?? []
        const paging = res.data.pagination_data

        setData(data)
        setPaging({ ...paging, limit: params.limit })
        onSuccess?.(paging)
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false))
  }

  const refetch = () => fetchData()

  useEffect(() => {
    fetchData()

    return () => {
      setData([])
      setLoading(false)
    }
  }, [params.limit, params.page, params.task_status, params.priority])

  return {
    data,
    paging,
    isLoading,
    refetch
  }
}

export function useTaskProgress(project_id: number) {
  const api = useApi()
  const [countCompletedTask, setCountCompletedTask] = useState(0)
  const [countPendingTask, setCountPendingTask] = useState(0)
  const [totalTask, setTotal] = useState(0)
  const [progressTask, setProgressTask] = useState(0)
  const [isLoading, setLoading] = useState(false)

  const fetchData = () => {
    setLoading(true)

    api
      .get("/task/", { params: { project_id: project_id } })
      .then((res: SuccessFetch<Response>) => {
        const data: TaskList[] = res.data.payload ?? []

        // Hitung jumlah berdasarkan task_status
        const grouped: Record<number, number> = {}
        data.forEach(item => {
          grouped[item.task_status] = (grouped[item.task_status] || 0) + 1
        })

        // Completed = status 4
        const completed = data.filter(item => item.task_status === 4).length

        // Pending = status 1,2,3,5
        const pending = data.filter(item =>
          [1, 2, 3, 5].includes(item.task_status)
        ).length

        const totalCount = data.length

        // Progress = (completed / total) * 100
        const progress =
          totalCount > 0 ? (completed / totalCount) * 100 : 0

        setCountCompletedTask(completed)
        setCountPendingTask(pending)
        setTotal(totalCount)
        setProgressTask(Math.round(progress))

      })
      .catch(() => {
        setCountCompletedTask(0)
        setCountPendingTask(0)
        setTotal(0)
        setProgressTask(0)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchData()

    return () => {
      setCountCompletedTask(0)
      setCountPendingTask(0)
      setTotal(0)
      setProgressTask(0)
      setLoading(false)
    }
  }, [project_id])

  return {
    countCompletedTask,
    countPendingTask,
    totalTask,
    progressTask,  
    isLoading,
    refetch: fetchData
  }
}
